import { Canceler } from 'axios';
import { action, makeObservable, observable } from 'mobx';
import { persist } from 'mobx-persist';
import moment from 'moment';
import { ordersStore } from '../ordersStore';
import { userStore } from '../userStore';
import { PER_PAGE, defaultPagination } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import { requests } from 'src/requests';
import client from 'src/services/client';
import { history } from 'src/services/history';
import { IBranch, IBranchFromSocket } from 'src/types/branches';
import { IPagination, TBooleanInNumber } from 'src/types/main';
import { IUser } from 'src/types/user';
import { objectMerge } from 'src/utils/formatter';
import { getCancelToken } from 'src/utils/helpers';
import { IBranchesModalItem } from 'src/view/containers/Modals/BranchesModal/BranchesModal';
import logger from '~/services/logger';
import { itemsInventoryDealStore } from '~/view/screens/ItemsInventoryScreen/screens/DealCombosScreen/store/itemsInventoryDealStore';
import { itemsInventoryMealStore } from '~/view/screens/ItemsInventoryScreen/screens/MealCombosScreen/store/itemsInventoryMealStore';
import { itemsInventoryProductStore } from '~/view/screens/ItemsInventoryScreen/screens/ProductsScreen/store/ItemsInventoryProductStore';
import { DELIVERY_TYPE } from '~/constants/deliveryTypes';
import branchesLogStore from '~/view/screens/BranchLogScreen/screens/BranchesLogScreen/store/branchesLogStore';

export class BranchStore {
  constructor() {
    makeObservable(this);
  }

  cancel: Canceler | null = null;

  public get persist() {
    return true;
  }

  @persist('list') @observable branches: IBranch[] = [];

  // TODO: swictch to multiple instead
  @observable pagination: IPagination<IBranch> = defaultPagination;
  @observable paginationAll: IPagination<IBranch> = defaultPagination;
  @observable lastUpdate: number = 0;

  @action fetchBranches = async (deliveryType?: DELIVERY_TYPE, cashing: boolean = true) => {
    try {
      // Data cashing
      // if branches fetched less than 1 min
      if (cashing && this.lastUpdate && this.pagination.data.length && moment().diff(moment(this.lastUpdate), 'minutes') < 1) {
        return;
      }
      this.pagination.loading = true;
      const pagination: IPagination<IBranch> = await client.get(`/v4/branches`, {
        params: {
          is_delete: 0,
          delivery_type: deliveryType === DELIVERY_TYPE.DINE_IN ? DELIVERY_TYPE.PICKUP : deliveryType,
          is_emenu: deliveryType === DELIVERY_TYPE.DINE_IN ? 1 : undefined,
          branches: userStore.user.branches.map((branch) => branch.id),
          per_page: PER_PAGE.MAXIMUM,
          status: 1,
        },
      });
      this.pagination = pagination;
      this.lastUpdate = Date.now();
      this.pagination.loading = false;
    } catch (error: any) {
      this.pagination.loading = false;
      logger.error({ error, place: 'BranchStore fetchBranches' });
    }
  };

  @action fetchAllBranches = async (simpleFetch: boolean = false, additionalParams: any = {}) => {
    try {
      this.paginationAll.loading = true;
      const paginationAll: IPagination<IBranch> = await client.get(`/v4/branches`, {
        params: {
          per_page: PER_PAGE.MAXIMUM,
          ...additionalParams,
        },
      });
      this.paginationAll.data = paginationAll.data;
      this.lastUpdate = Date.now();
      if (!simpleFetch && !branchesLogStore.filters.branchIds?.length) {
        await branchesLogStore.selectAllBranches();
      }
      this.paginationAll.loading = false;
    } catch (error: any) {
      this.paginationAll.loading = false;
      logger.error({ error, place: 'BranchStore fetchAllBranches' });
    }
  };

  @action fetchBranch = async () => {
    try {
      if (this.cancel) this.cancel('Cancelled');
      const branches: IPagination<IBranch> = await client.get(`/v4/branches`, {
        headers: { 'Accept-Language': 'en,ar' },
        params: { is_delete: 0, branches: userStore.user.branches.map((branch) => branch.id), per_page: PER_PAGE.MAXIMUM },
        cancelToken: getCancelToken((c) => (this.cancel = c)),
      });
      this.branches = branches.data;
      this.pagination.data = branches.data.filter((branch) => branch.status);
    } catch (error: any) {
      if (error?.message === 'Cancelled') return;
      logger.error({ error, place: 'BranchStore fetchBranch' });
    }
  };

  @action update = async (branch: IBranchFromSocket) => {
    // is_delete change handler
    if (branch.updated_at && branch.is_delete !== undefined) {
      if (branch.is_delete) {
        this.branches = this.branches.filter((_branch) => branch.id !== _branch.id);
        this.pagination.data = this.pagination.data.filter((_branch) => branch.id !== _branch.id);
        userStore.user.branches = userStore.user.branches.filter((_branch) => branch.id !== _branch.id);
        if (!userStore.branchesAvailable()) return;
      } else {
        await userStore.updateUser();
        this.fetchBranches();
      }
      this.updateBranchesFilters(branch.id, { is_delete: branch.is_delete });
    }

    // status change handler
    if (branch.updated_at && branch.status !== undefined) {
      if (!branch.status) {
        this.pagination.data = this.pagination.data.filter((_branch) => branch.id !== _branch.id);
      } else {
        this.pagination.data = this.pagination.data.concat(this.branches.find((_branch) => _branch.id === branch.id) || []);
      }

      this.branches = this.branches.map((item) => (item.id === branch.id ? { ...item, status: branch.status || 0 } : item));
      userStore.user.branches = userStore.user.branches.map((item) => (item.id === branch.id ? { ...item, status: branch.status || 0 } : item));
      this.updateBranchesFilters(branch.id, { status: branch.status });
    }

    // busy change handler
    if (branch.is_busy !== undefined) {
      this.branches = this.branches.map((item) => (item.id === branch.id ? (item = objectMerge(item, branch, ['is_busy'])) : item));
      this.pagination.data = this.pagination.data.map((_branch) => (_branch.id === branch.id ? objectMerge(_branch, branch, ['is_busy']) : _branch));
    }
  };

  @action public updateBranchesFilters = (id?: number, params?: { status?: TBooleanInNumber; is_delete?: TBooleanInNumber }) => {
    // orders filter
    let ordersBranchesFilter: IBranchesModalItem[] = ordersStore.branchesFilter;
    // via socket
    if (id) {
      // status
      // if (params?.status === '0') {
      //   ordersBranchesFilter = ordersStore.branchesFilter.filter((branch) => branch.id !== id);
      // } else if (!ordersBranchesFilter.find((branch) => branch.id === id)) {
      //   ordersBranchesFilter = ordersStore.branchesFilter.concat(this.branches.find((branch) => branch.id === id) || []);
      // }
      // is_delete
      if (params?.is_delete) {
        ordersBranchesFilter = ordersStore.branchesFilter.filter((branch) => branch.id !== id);
      } else if (!ordersBranchesFilter.find((branch) => branch.id === id)) {
        ordersBranchesFilter = ordersStore.branchesFilter.concat(this.branches.find((branch) => branch.id === id) || []);
      }
    }
    // at login or refresh page
    else {
      if (ordersStore.branchesFilter.length) {
        ordersBranchesFilter = ordersStore.branchesFilter.reduce(
          (sum: IBranchesModalItem[], current) => sum.concat(userStore.user.branches.find((item) => item.id === current.id) || []),
          []
        );
      } else {
        ordersBranchesFilter = userStore.user.branches;
      }
    }

    if (window.location.pathname.includes(SCREENS.LIVE_ORDERS())) {
      const order_id = history.location.pathname.split('/')[3];
      ordersStore.setFilters({}, false, { order_id: order_id || undefined, branches: ordersBranchesFilter });
    } else {
      ordersStore.branchesFilter = ordersBranchesFilter;
    }

    itemsInventoryProductStore.handleBranchUpdateBySocket();
    itemsInventoryDealStore.handleBranchUpdateBySocket();
    itemsInventoryMealStore.handleBranchUpdateBySocket();

    this.fetchBranch();
  };

  @action setBranchBusy = async (id: number, busy: boolean) => {
    try {
      if (this.branches.length < 1) return;
      this.branches.map((item) => (item.id === id ? (item.is_busy = busy) : item));
      await client.patch(`/v4/branches/${id}`, {
        is_busy: Number(busy),
      });
      return true;
    } catch (error: any) {
      this.branches.map((item) => (item.id === id ? (item.is_busy = !busy) : item));
      logger.error({ error, place: 'BranchStore setBranchBusy' });
      return false;
    }
  };

  @action clear = () => {
    this.pagination = defaultPagination;
    this.lastUpdate = 0;
    this.branches = [];
  };
}

const branchStore = new BranchStore();
export { branchStore };
